// Pray-mode scripture anchor builder (Bible reader Pray Mode).
//
// Pure helper that turns the in-memory BSB chapter blocks the reader
// already holds, plus a user selection (specific verses or whole
// chapter), into a `ScriptureAnchor` snapshot for a Prayer record. The
// app NEVER suggests or picks scripture; this only records what the user
// explicitly selected.
//
// Probe-importable leaf module, same posture as `bsb-parser.ts` and
// `bsb-search-core.ts`: it imports RELATIVELY from `./bsb-types` and
// `../types/prayer` (NOT the `@/lib/bible/active` facade, NOT any `@/`
// alias) and pulls in no React and no IDB, so
// `tsx scripts/probe-scripture-anchor.mjs` loads it with default
// resolution. The facade rule (#65) governs UI surfaces and feature
// code; this is an implementation-tier leaf the probe must load
// directly, mirroring the existing probe-importable bsb-* leaves.
// `bsb-types` has zero internal imports, so the runtime
// `CANONICAL_SLUG_TO_NAME` import is safe under tsx.
//
// Snapshot posture (cf. order snapshots, scaled down): `bookName`,
// `reference`, and `text` are frozen at capture so surfaces render the
// prayer identically without re-fetching BSB even if the bundled
// translation changes later. `rawText` is never touched here; the
// composite "intro + scripture + prayer" body is a display-time
// composition elsewhere.

import { CANONICAL_SLUG_TO_NAME } from './bsb-types';
import type { BsbBlock, BsbVerse, TextSegment } from './bsb-types';
import type { ScriptureAnchor } from '../types/prayer';

/**
 * What the user selected in Pray Mode. `verses` carries the tapped verse
 * numbers (order and duplicates are irrelevant; normalized internally).
 * `chapter` is the whole-chapter path.
 */
export type ScriptureSelection =
  | { kind: 'verses'; verses: number[] }
  | { kind: 'chapter' };

export interface BuildScriptureAnchorInput {
  bookSlug: string;
  chapter: number;
  /** The reader's in-memory chapter blocks (already hydrated). */
  blocks: BsbBlock[];
  selection: ScriptureSelection;
}

// Flatten a verse's text (string or TextSegment[]) to a plain string.
// Mirrors `segmentsToText` in `bsb-search-core.ts` (module-private
// there; the two probe-importable leaves deliberately do not
// cross-import, per the rule #4 cross-boundary-duplication sub-rule).
function segmentsToText(t: string | TextSegment[]): string {
  if (typeof t === 'string') return t;
  return t.map((seg) => seg.text).join('');
}

interface ExtractedVerse {
  v: number;
  text: string;
}

// Walk blocks in document order, aggregate vCont fragments by verse
// number, drop omitted verses. Document order is preserved (Mark
// 9:43-46 emits non-ascending source order; the Map keeps insertion
// order). Mirrors `extractVersesForSearch` in `bsb-search-core.ts`;
// re-implemented rather than shared because that helper is
// module-private and the two leaf modules stay independent (rule #4
// cross-boundary-duplication sub-rule).
function extractChapterVerses(blocks: BsbBlock[]): ExtractedVerse[] {
  const byVerse = new Map<number, { text: string; omitted: boolean }>();

  const collect = (verse: BsbVerse): void => {
    if (verse.omitted === true) {
      if (!byVerse.has(verse.v)) {
        byVerse.set(verse.v, { text: '', omitted: true });
      }
      return;
    }
    const text = segmentsToText(verse.t);
    const existing = byVerse.get(verse.v);
    if (existing) {
      existing.text = existing.text ? existing.text + ' ' + text : text;
      existing.omitted = false;
    } else {
      byVerse.set(verse.v, { text, omitted: false });
    }
  };

  for (const block of blocks) {
    if (block.type === 'paragraph') {
      for (const verse of block.verses) collect(verse);
    } else if (block.type === 'poetry') {
      for (const line of block.lines) {
        for (const verse of line.verses) collect(verse);
      }
    }
    // acrostic / title / section / majorSection / reference / blank
    // carry no verses.
  }

  const result: ExtractedVerse[] = [];
  for (const [v, entry] of byVerse) {
    if (entry.omitted) continue;
    result.push({ v, text: entry.text });
  }
  return result;
}

/**
 * Resolve every non-omitted verse in a chapter's blocks to its plain text,
 * keyed by verse number. Reuses `extractChapterVerses` (vCont aggregation,
 * omitted drop, document order). The Scripture Journal passage / meaning pages
 * load a chapter's blocks once (`ensureBibleHydrated` then `getChapter`) and
 * resolve each marked verse's text from this Map (no per-verse IDB reads, no
 * re-extraction). BSB stays the single text source (rule #62/#76); the leaf
 * stays probe-clean (structure only, async/IDB loading lives in the page).
 */
export function chapterVerseTexts(blocks: BsbBlock[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const { v, text } of extractChapterVerses(blocks)) {
    map.set(v, text);
  }
  return map;
}

/**
 * Collapse a verse-number list into a citation range string: contiguous
 * runs become "16-18", gaps stay separated ("16, 18, 20"), a single
 * verse is just "16", and "16-17, 20" mixes both. Input may be unsorted
 * or carry duplicates; output is normalized to ascending unique. Returns
 * '' for an empty list.
 */
export function formatVerseRanges(verses: number[]): string {
  const sorted = Array.from(new Set(verses)).sort((a, b) => a - b);
  if (sorted.length === 0) return '';

  const runs: string[] = [];
  let runStart = sorted[0];
  let runEnd = sorted[0];

  const flush = (): void => {
    runs.push(runStart === runEnd ? `${runStart}` : `${runStart}-${runEnd}`);
  };

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === runEnd + 1) {
      runEnd = sorted[i];
    } else {
      flush();
      runStart = sorted[i];
      runEnd = sorted[i];
    }
  }
  flush();
  return runs.join(', ');
}

/**
 * Validate and detach a ScriptureAnchor read from a persistence boundary.
 *
 * The shape is additive in several durable records (Prayer, browser voice
 * recovery, and queued bookmark audio), so those readers cannot trust a cast.
 * Internal consistency is checked as well as primitive types: a malformed
 * reference can never be attached to otherwise-valid recovered words.
 */
export function parseScriptureAnchor(value: unknown): ScriptureAnchor | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  // Complete lifecycle documents canonicalize an absent `wholeChapter` to
  // null. Treat that durable representation exactly like undefined/false.
  if (
    typeof candidate.bookSlug !== 'string' || !candidate.bookSlug.trim() ||
    typeof candidate.bookName !== 'string' || !candidate.bookName.trim() ||
    !Number.isSafeInteger(candidate.chapter) || (candidate.chapter as number) <= 0 ||
    !Array.isArray(candidate.verses) ||
    candidate.verses.some(
      (verse) => !Number.isSafeInteger(verse) || (verse as number) <= 0,
    ) ||
    typeof candidate.reference !== 'string' || !candidate.reference.trim() ||
    typeof candidate.text !== 'string' || !candidate.text.trim() ||
    candidate.translation !== 'BSB' ||
    (candidate.wholeChapter !== undefined &&
      candidate.wholeChapter !== null &&
      typeof candidate.wholeChapter !== 'boolean')
  ) {
    return null;
  }

  const bookSlug = candidate.bookSlug;
  const bookName = candidate.bookName;
  const chapter = candidate.chapter as number;
  const verses = candidate.verses as number[];
  const wholeChapter = candidate.wholeChapter === true;
  const expectedBookName = CANONICAL_SLUG_TO_NAME.get(bookSlug) ?? bookSlug;
  const normalizedVerses = [...new Set(verses)].sort((a, b) => a - b);
  if (
    bookName !== expectedBookName ||
    normalizedVerses.length !== verses.length ||
    normalizedVerses.some((verse, index) => verse !== verses[index]) ||
    (wholeChapter ? verses.length !== 0 : verses.length === 0)
  ) {
    return null;
  }

  const expectedReference = wholeChapter
    ? `${bookName} ${chapter}`
    : `${bookName} ${chapter}:${formatVerseRanges(verses)}`;
  if (candidate.reference !== expectedReference) return null;

  return {
    bookSlug,
    bookName,
    chapter,
    verses: [...verses],
    ...(wholeChapter ? { wholeChapter: true } : {}),
    reference: candidate.reference,
    text: candidate.text,
    translation: 'BSB',
  };
}

/** Detach a trusted capture option so later UI mutation cannot retag audio. */
export function cloneScriptureAnchor(anchor: ScriptureAnchor): ScriptureAnchor {
  return {
    ...anchor,
    verses: [...anchor.verses],
  };
}

/** Exact frozen-passage identity used before offering queued bookmark words. */
export function scriptureAnchorsEqual(
  left: ScriptureAnchor,
  right: ScriptureAnchor,
): boolean {
  return (
    left.bookSlug === right.bookSlug &&
    left.bookName === right.bookName &&
    left.chapter === right.chapter &&
    left.wholeChapter === right.wholeChapter &&
    left.reference === right.reference &&
    left.text === right.text &&
    left.translation === right.translation &&
    left.verses.length === right.verses.length &&
    left.verses.every((verse, index) => verse === right.verses[index])
  );
}

export interface FrozenVerseFallback {
  anchor: ScriptureAnchor;
  /** False means the historical text framing was ambiguous, so `anchor` is
   *  the complete frozen passage rather than a guessed single-verse slice. */
  exact: boolean;
}

/**
 * Recover one verse from an already-frozen anchor without ever treating a
 * number in Scripture prose as a structural boundary. Historical anchors used
 * a bare `"<verse> "` prefix, which is intrinsically ambiguous for text such as
 * “12 in all.” We slice only when every selected verse marker occurs exactly
 * once and in order. Otherwise callers receive the complete frozen passage and
 * can keep it readable while disabling any new single-verse persistence.
 */
export function singleVerseFromFrozenAnchor(
  frozen: ScriptureAnchor,
  verse: number,
): FrozenVerseFallback | null {
  const index = frozen.verses.indexOf(verse);
  if (index < 0) return null;
  if (frozen.verses.length === 1) {
    return {
      exact: true,
      anchor: {
        ...frozen,
        wholeChapter: undefined,
        reference: `${frozen.bookName} ${frozen.chapter}:${verse}`,
      },
    };
  }

  const markerStarts = (value: number): number[] => {
    const marker = new RegExp(`(?:^|\\s)${value}\\s`, 'g');
    const starts: number[] = [];
    let match: RegExpExecArray | null;
    while ((match = marker.exec(frozen.text)) !== null) {
      starts.push(match.index + (match[0].startsWith(`${value}`) ? 0 : 1));
    }
    return starts;
  };
  const positions = frozen.verses.map(markerStarts);
  const unambiguous =
    positions.every((matches) => matches.length === 1) &&
    positions.every((matches, position) =>
      position === 0 || matches[0] > positions[position - 1][0],
    );
  if (!unambiguous) return { anchor: frozen, exact: false };

  const start = positions[index][0];
  const next = positions[index + 1]?.[0] ?? -1;
  return {
    exact: true,
    anchor: {
      ...frozen,
      verses: [verse],
      wholeChapter: undefined,
      reference: `${frozen.bookName} ${frozen.chapter}:${verse}`,
      text: frozen.text.slice(start, next < 0 ? undefined : next).trim(),
    },
  };
}

/**
 * Build a `ScriptureAnchor` from the reader's in-memory chapter blocks
 * and the user's selection. Returns `null` when nothing in scope
 * resolves (empty selection, every selected verse missing or omitted, or
 * an empty/blank chapter) so the caller can no-op cleanly.
 *
 * `bookName` comes from `CANONICAL_SLUG_TO_NAME` (verse-reference
 * register: 'Psalm', not 'Psalms'), falling back to the slug if unknown.
 * `text` number-prefixes each verse ("16 In the beginning ...") so a
 * scattered selection stays unambiguous; whole-chapter text follows
 * document order, selected-verse text follows ascending order so
 * `verses`, `reference`, and `text` all agree.
 */
export function buildScriptureAnchor(
  input: BuildScriptureAnchorInput,
): ScriptureAnchor | null {
  const { bookSlug, chapter, blocks, selection } = input;
  const bookName = CANONICAL_SLUG_TO_NAME.get(bookSlug) ?? bookSlug;

  const chapterVerses = extractChapterVerses(blocks);
  if (chapterVerses.length === 0) return null;

  if (selection.kind === 'chapter') {
    const text = chapterVerses.map((e) => `${e.v} ${e.text}`).join(' ');
    return {
      bookSlug,
      bookName,
      chapter,
      verses: [],
      wholeChapter: true,
      reference: `${bookName} ${chapter}`,
      text,
      translation: 'BSB',
    };
  }

  // Verses scope: keep only selected verses that actually exist and are
  // non-omitted in this chapter, ascending.
  const present = new Map(chapterVerses.map((e) => [e.v, e.text] as const));
  const selected = Array.from(new Set(selection.verses))
    .filter((v) => present.has(v))
    .sort((a, b) => a - b);
  if (selected.length === 0) return null;

  const text = selected.map((v) => `${v} ${present.get(v) ?? ''}`).join(' ');
  return {
    bookSlug,
    bookName,
    chapter,
    verses: selected,
    reference: `${bookName} ${chapter}:${formatVerseRanges(selected)}`,
    text,
    translation: 'BSB',
  };
}
